import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
}

export const CookieConsent: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true,
    analytics: false,
    marketing: false,
  });

  const getStorageItem = (key: string): string | null => {
    try {
      return localStorage.getItem(key) || sessionStorage.getItem(key);
    } catch (e) {
      // localStorage might not be available in private mode
      try {
        return sessionStorage.getItem(key);
      } catch (err) {
        return null;
      }
    }
  };

  const setStorageItem = (key: string, value: string): void => {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      // Fallback to sessionStorage in private mode
      try {
        sessionStorage.setItem(key, value);
      } catch (err) {
        console.warn('Storage not available');
      }
    }
  };

  useEffect(() => {
    const consent = getStorageItem('cookieConsent');
    if (!consent) {
      // Small delay to ensure page is loaded
      setTimeout(() => setShowBanner(true), 500);
    }
  }, []);

  const acceptCookies = () => {
    const allAccepted = { essential: true, analytics: true, marketing: true };
    setStorageItem('cookieConsent', 'accepted');
    setStorageItem('cookiePreferences', JSON.stringify(allAccepted));
    setShowBanner(false);
    setShowPreferences(false);
  };

  const declineCookies = () => {
    const onlyEssential = { essential: true, analytics: false, marketing: false };
    setStorageItem('cookieConsent', 'declined');
    setStorageItem('cookiePreferences', JSON.stringify(onlyEssential));
    setShowBanner(false);
    setShowPreferences(false);
  };

  const savePreferences = () => {
    setStorageItem('cookieConsent', 'custom');
    setStorageItem('cookiePreferences', JSON.stringify(preferences));
    setShowBanner(false);
    setShowPreferences(false);
  };

  const openPreferences = () => {
    setShowPreferences(true);
  };

  const closePreferences = () => {
    setShowPreferences(false);
  };

  const togglePreference = (key: keyof CookiePreferences) => {
    if (key === 'essential') return; // Essential cookies cannot be disabled
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (!showBanner && !showPreferences) return null;

  return (
    <>
    {showBanner && (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="fixed bottom-0 left-0 right-0 z-50 bg-charcoal/95 backdrop-blur-md text-ivory p-4 md:p-6 shadow-2xl border-t border-bronze/20 rounded-t-2xl"
    >
          <div className="container mx-auto max-w-6xl">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex-1">
                <h3 className="font-cormorant text-xl md:text-2xl mb-2">Cookie Notice</h3>
                <p className="text-sm md:text-base text-ivory/80 leading-relaxed">
                  We use essential cookies to ensure our website functions properly and to improve your experience. 
                  By clicking "Accept", you consent to our use of cookies. You can manage your preferences at any time. 
                  Read our{' '}
                  <a href="/privacy-policy" className="text-bronze hover:underline">
                    Privacy Policy
                  </a>{' '}
                  for more information.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                <button
                  onClick={openPreferences}
                  className="px-6 py-2 bg-transparent border border-ivory/30 text-ivory rounded-md hover:bg-ivory/10 transition-colors text-sm font-medium whitespace-nowrap"
                >
                  Change Settings
                </button>
                <button
                  onClick={declineCookies}
                  className="px-6 py-2 bg-transparent border border-ivory/30 text-ivory rounded-md hover:bg-ivory/10 transition-colors text-sm font-medium whitespace-nowrap"
                >
                  Decline
                </button>
                <button
                  onClick={acceptCookies}
                  className="px-6 py-2 bg-bronze text-ivory rounded-md hover:bg-bronze/80 transition-colors text-sm font-medium whitespace-nowrap"
                >
                  Accept All
                </button>
              </div>
            </div>
          </div>
    </motion.div>
    )}

    {/* Cookie Preferences Modal */}
    {showPreferences && (
      <>
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]"
          onClick={closePreferences}
        />
        
        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[70] w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-ivory rounded-2xl shadow-2xl"
        >
          <div className="p-6 md:p-8">
            {/* Header */}
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="font-cormorant text-3xl font-semibold mb-2">Cookie Preferences</h2>
                <p className="text-sm text-taupe">Manage your cookie settings</p>
              </div>
              <button
                onClick={closePreferences}
                className="text-taupe hover:text-charcoal transition-colors text-2xl leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {/* Cookie Options */}
            <div className="space-y-4 mb-6">
              {/* Essential Cookies */}
              <div className="bg-sand/30 p-4 rounded-lg">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="font-medium text-lg mb-1">Essential Cookies</h3>
                    <p className="text-sm text-taupe leading-relaxed">
                      Required for the website to function properly. These cannot be disabled.
                    </p>
                  </div>
                  <div className="ml-4">
                    <div className="w-12 h-6 bg-bronze rounded-full flex items-center px-1">
                      <div className="w-4 h-4 bg-white rounded-full ml-auto"></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="bg-white p-4 rounded-lg border border-taupe/20">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="font-medium text-lg mb-1">Analytics Cookies</h3>
                    <p className="text-sm text-taupe leading-relaxed">
                      Help us understand how visitors interact with our website by collecting and reporting information anonymously.
                    </p>
                  </div>
                  <button
                    onClick={() => togglePreference('analytics')}
                    className="ml-4"
                    aria-label="Toggle analytics cookies"
                  >
                    <div className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                      preferences.analytics ? 'bg-bronze' : 'bg-taupe/30'
                    }`}>
                      <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                        preferences.analytics ? 'ml-auto' : ''
                      }`}></div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Marketing Cookies */}
              <div className="bg-white p-4 rounded-lg border border-taupe/20">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="font-medium text-lg mb-1">Marketing Cookies</h3>
                    <p className="text-sm text-taupe leading-relaxed">
                      Used to track visitors across websites to display relevant and engaging advertisements.
                    </p>
                  </div>
                  <button
                    onClick={() => togglePreference('marketing')}
                    className="ml-4"
                    aria-label="Toggle marketing cookies"
                  >
                    <div className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                      preferences.marketing ? 'bg-bronze' : 'bg-taupe/30'
                    }`}>
                      <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                        preferences.marketing ? 'ml-auto' : ''
                      }`}></div>
                    </div>
                  </button>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={closePreferences}
                className="flex-1 px-6 py-3 bg-transparent border border-taupe/30 text-charcoal rounded-md hover:bg-sand/30 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={savePreferences}
                className="flex-1 px-6 py-3 bg-bronze text-ivory rounded-md hover:bg-bronze/80 transition-colors font-medium"
              >
                Save Preferences
              </button>
            </div>

            {/* Privacy Policy Link */}
            <p className="text-xs text-taupe text-center mt-4">
              For more information, read our{' '}
              <a href="/privacy-policy" className="text-bronze hover:underline">
                Privacy Policy
              </a>
            </p>
          </div>
        </motion.div>
      </>
    )}
  </>
  );
};
